import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api, { getApiErrorMessage } from '../services/api';
import { toast } from 'sonner';
import { Package, Plus, AlertTriangle, CheckCircle2, XCircle, Ban, Save, Shield, RotateCcw, Truck, Trash2, Clock, ShieldAlert } from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../components/SortableTableHeader';

const STATUS_STYLES = {
  ACTIVE: 'bg-emerald-100 text-emerald-700',
  EXPIRING: 'bg-amber-100 text-amber-700',
  EXPIRED: 'bg-red-100 text-red-700',
  QUARANTINED: 'bg-orange-100 text-orange-700',
  PENDING_WRITEOFF: 'bg-purple-100 text-purple-700',
  WRITTEN_OFF: 'bg-slate-100 text-slate-500',
  RETURNED_TO_SUPPLIER: 'bg-cyan-100 text-cyan-700',
  CONSUMED: 'bg-blue-100 text-blue-600',
};

const STATUS_ICONS = {
  ACTIVE: CheckCircle2,
  EXPIRING: Clock,
  EXPIRED: XCircle,
  QUARANTINED: ShieldAlert,
  PENDING_WRITEOFF: AlertTriangle,
  WRITTEN_OFF: Ban,
  RETURNED_TO_SUPPLIER: Truck,
};

const SERIAL_STATUS_STYLES = {
  IN_STOCK: 'bg-emerald-100 text-emerald-700',
  ISSUED: 'bg-blue-100 text-blue-700',
  ON_HOLD: 'bg-orange-100 text-orange-700',
  RETURNED: 'bg-teal-100 text-teal-700',
  SCRAPPED: 'bg-red-100 text-red-600',
  RETURNED_TO_SUPPLIER: 'bg-cyan-100 text-cyan-700',
  WRITTEN_OFF: 'bg-slate-100 text-slate-500',
};

export default function BatchManagement({ itemCode }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [autoExpiring, setAutoExpiring] = useState(false);
  const [sortState, handleSort] = useSortState('batch_number', 'asc');

  const loadBatches = useCallback(async () => {
    try {
      const r = await api.get(`/api/inventory/items/${itemCode}/batches`);
      setBatches(r.data.data || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [itemCode]);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  const runAutoExpire = async () => {
    setAutoExpiring(true);
    try {
      const r = await api.post('/api/inventory/batches/auto-expire');
      toast.success(r.data.message, { duration: 6000 });
      loadBatches();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Auto-expire failed')); }
    setAutoExpiring(false);
  };

  const activeBatches = batches.filter(b => b.status === 'ACTIVE');
  const expiringBatches = batches.filter(b => b.status === 'EXPIRING');
  const expiredBatches = batches.filter(b => b.status === 'EXPIRED');
  const quarantinedBatches = batches.filter(b => b.status === 'QUARANTINED' || b.status === 'PENDING_WRITEOFF');
  const disposedBatches = batches.filter(b => ['WRITTEN_OFF', 'RETURNED_TO_SUPPLIER'].includes(b.status));
  const totalRemaining = batches.reduce((s, b) => s + (b.quantity_remaining || 0), 0);

  if (loading) return <div className="py-8 text-center text-sm text-muted-foreground">Loading batches...</div>;

  return (
    <div className="space-y-4" data-testid="batch-management">
      {/* Summary cards */}
      <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
        <SummaryCard label="Total" value={batches.length} />
        <SummaryCard label="Active" value={activeBatches.length} accent="emerald" />
        <SummaryCard label="Expiring" value={expiringBatches.length} accent={expiringBatches.length > 0 ? 'amber' : 'slate'} />
        <SummaryCard label="Expired" value={expiredBatches.length} accent={expiredBatches.length > 0 ? 'red' : 'slate'} />
        <SummaryCard label="Quarantined" value={quarantinedBatches.length} accent={quarantinedBatches.length > 0 ? 'orange' : 'slate'} />
        <SummaryCard label="Qty Remaining" value={totalRemaining} accent="blue" />
        <div className="border rounded-lg p-2 flex flex-col items-center justify-center gap-1">
          <Button size="sm" onClick={() => setShowCreate(true)} className="w-full h-6 text-[10px]" data-testid="create-batch-btn">
            <Plus className="h-3 w-3 mr-0.5" /> New Batch
          </Button>
          <Button size="sm" variant="outline" onClick={runAutoExpire} disabled={autoExpiring} className="w-full h-6 text-[10px]" data-testid="auto-expire-btn">
            <Clock className="h-3 w-3 mr-0.5" /> {autoExpiring ? 'Scanning...' : 'Auto-Expire'}
          </Button>
        </div>
      </div>

      {/* Batches table */}
      {batches.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground border rounded-xl">
          <Package className="h-8 w-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No batches recorded for this item</p>
          <p className="text-xs mt-1">Create a batch when stock is received to track lot numbers, expiry dates, and quantities</p>
        </div>
      ) : (
        <div className="border rounded-xl overflow-hidden">
          <table className="w-full text-xs" data-testid="batches-table">
            <thead><tr className="bg-muted/50 border-b">
              <SortableTableHeader label="Batch #" field="batch_number" sortState={sortState} onSort={handleSort} />
              <SortableTableHeader label="Lot #" field="lot_number" sortState={sortState} onSort={handleSort} />
              <SortableTableHeader label="Location" field="location_code" sortState={sortState} onSort={handleSort} />
              <SortableTableHeader label="Manufacturer" field="manufacturer" sortState={sortState} onSort={handleSort} />
              <SortableTableHeader label="Expiry Date" field="expiry_date" sortState={sortState} onSort={handleSort} />
              <SortableTableHeader label="Days Left" field="_days_left" sortState={sortState} onSort={handleSort} className="text-center" />
              <SortableTableHeader label="Qty Received" field="original_quantity" sortState={sortState} onSort={handleSort} className="text-right" />
              <SortableTableHeader label="Qty Remaining" field="quantity_remaining" sortState={sortState} onSort={handleSort} className="text-right" />
              <SortableTableHeader label="Status" field="status" sortState={sortState} onSort={handleSort} />
              <th className="text-center px-3 py-2 font-semibold">Actions</th>
            </tr></thead>
            <tbody>
              {sortData(batches.map(b => ({...b, _days_left: b.expiry_date ? Math.ceil((new Date(b.expiry_date) - new Date()) / (1000 * 60 * 60 * 24)) : null})), sortState.field, sortState.dir).map((b) => {
                const daysLeft = b._days_left;
                const isExpiringSoon = daysLeft !== null && daysLeft <= 30 && daysLeft > 0;
                const isExpired = daysLeft !== null && daysLeft <= 0;
                const StatusIcon = STATUS_ICONS[b.status] || Package;
                const canQuarantine = ['ACTIVE', 'EXPIRING', 'EXPIRED'].includes(b.status) && (b.quantity_remaining || 0) > 0;
                const canDispose = ['QUARANTINED', 'EXPIRED', 'EXPIRING'].includes(b.status);
                const isTerminal = ['WRITTEN_OFF', 'RETURNED_TO_SUPPLIER', 'PENDING_WRITEOFF'].includes(b.status);

                return (
                  <tr key={`${b.batch_number}-${b.location_code}`}
                    className={`border-b transition-colors hover:bg-accent/20 cursor-pointer ${isExpired && !isTerminal ? 'bg-red-50/50' : isExpiringSoon && !isTerminal ? 'bg-amber-50/30' : ''}`}
                    onClick={() => setEditingBatch(b)}
                    data-testid={`batch-row-${b.batch_number}`}>
                    <td className="px-3 py-2 font-mono font-semibold text-primary underline decoration-dotted underline-offset-2">{b.batch_number}</td>
                    <td className="px-3 py-2 text-muted-foreground">{b.lot_number || '—'}</td>
                    <td className="px-3 py-2 font-semibold">{b.location_code}</td>
                    <td className="px-3 py-2">{b.manufacturer || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={isExpired ? 'text-red-600 font-bold' : isExpiringSoon ? 'text-amber-600 font-semibold' : ''}>
                        {b.expiry_date ? fmtDate(b.expiry_date) : '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {daysLeft !== null ? (
                        <span className={`font-semibold ${daysLeft <= 0 ? 'text-red-600' : daysLeft <= 30 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                          {daysLeft <= 0 ? `${Math.abs(daysLeft)}d ago` : `${daysLeft}d`}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">{b.quantity ?? '—'}</td>
                    <td className="px-3 py-2 text-right font-semibold">{b.quantity_remaining ?? '—'}</td>
                    <td className="px-3 py-2">
                      <Badge className={`text-[9px] ${STATUS_STYLES[b.status] || 'bg-slate-100'}`}>
                        <StatusIcon className="h-2.5 w-2.5 mr-0.5 inline" />
                        {b.status?.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td className="px-3 py-1.5 text-center" onClick={e => e.stopPropagation()}>
                      <div className="flex gap-1 justify-center">
                        {canQuarantine && (
                          <QuarantineBtn batchNumber={b.batch_number} onDone={loadBatches} />
                        )}
                        {canDispose && (
                          <DisposeBtn batch={b} onDone={loadBatches} />
                        )}
                        {isTerminal && <span className="text-[9px] text-muted-foreground italic">Closed</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot><tr className="border-t-2 font-semibold bg-muted/30">
              <td colSpan={6} className="px-3 py-2 text-right">Totals</td>
              <td className="px-3 py-2 text-right">{batches.reduce((s, b) => s + (b.quantity || 0), 0)}</td>
              <td className="px-3 py-2 text-right">{totalRemaining}</td>
              <td colSpan={2}></td>
            </tr></tfoot>
          </table>
        </div>
      )}

      <CreateBatchDialog open={showCreate} onClose={() => setShowCreate(false)}
        itemCode={itemCode} onCreated={() => { setShowCreate(false); loadBatches(); }} />

      {editingBatch && (
        <EditBatchDialog batch={editingBatch} onClose={() => setEditingBatch(null)}
          onSaved={() => { setEditingBatch(null); loadBatches(); }} />
      )}
    </div>
  );
}


// ── Quarantine Button ──

function QuarantineBtn({ batchNumber, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('Expired stock');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/api/inventory/batches/${batchNumber}/quarantine`, { reason });
      toast.success(r.data.message, { duration: 6000 });
      setOpen(false);
      onDone();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
    setSubmitting(false);
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}
        className="h-5 text-[9px] px-1.5 border-orange-300 text-orange-700 hover:bg-orange-50"
        data-testid={`quarantine-btn-${batchNumber}`}>
        <Shield className="h-2.5 w-2.5 mr-0.5" />Quarantine
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-sm">Quarantine Batch {batchNumber}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">This will remove the batch from available stock and place associated serial numbers on hold.</p>
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Reason</Label>
            <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} className="text-xs" data-testid="quarantine-reason" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={submit} disabled={submitting} className="bg-orange-600 hover:bg-orange-700" data-testid="quarantine-confirm">
              {submitting ? 'Processing...' : 'Confirm Quarantine'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ── Dispose Button (Release / Write-off / Return to Supplier) ──

function DisposeBtn({ batch, onDone }) {
  const [open, setOpen] = useState(false);
  const [disposition, setDisposition] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!disposition) { toast.error('Select a disposition action'); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/api/inventory/batches/${batch.batch_number}/dispose`, { disposition, reason });
      toast.success(r.data.message, { duration: 8000 });
      setOpen(false);
      onDone();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
    setSubmitting(false);
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}
        className="h-5 text-[9px] px-1.5 border-purple-300 text-purple-700 hover:bg-purple-50"
        data-testid={`dispose-btn-${batch.batch_number}`}>
        Dispose
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-sm">Dispose Batch {batch.batch_number}</DialogTitle></DialogHeader>
          <div className="text-xs space-y-1 bg-muted/50 rounded-lg p-3">
            <p><span className="text-muted-foreground">Status:</span> <Badge className={`text-[9px] ${STATUS_STYLES[batch.status]}`}>{batch.status}</Badge></p>
            <p><span className="text-muted-foreground">Qty Remaining:</span> <strong>{batch.quantity_remaining}</strong></p>
            <p><span className="text-muted-foreground">Location:</span> {batch.location_code}</p>
          </div>

          <div className="space-y-3 pt-1">
            <Label className="text-xs font-semibold">Disposition Action</Label>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => setDisposition('release')}
                className={`flex flex-col items-center gap-1 p-3 border rounded-lg text-xs transition-colors ${disposition === 'release' ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-300' : 'hover:bg-accent/20'}`}
                data-testid="dispose-release">
                <RotateCcw className="h-5 w-5 text-emerald-600" />
                <span className="font-semibold text-emerald-700">Release</span>
                <span className="text-[9px] text-muted-foreground text-center">Return to active stock</span>
              </button>
              <button onClick={() => setDisposition('write_off')}
                className={`flex flex-col items-center gap-1 p-3 border rounded-lg text-xs transition-colors ${disposition === 'write_off' ? 'border-red-500 bg-red-50 ring-2 ring-red-300' : 'hover:bg-accent/20'}`}
                data-testid="dispose-writeoff">
                <Trash2 className="h-5 w-5 text-red-600" />
                <span className="font-semibold text-red-700">Write Off</span>
                <span className="text-[9px] text-muted-foreground text-center">Destroy / dispose (approval req.)</span>
              </button>
              <button onClick={() => setDisposition('return_to_supplier')}
                className={`flex flex-col items-center gap-1 p-3 border rounded-lg text-xs transition-colors ${disposition === 'return_to_supplier' ? 'border-cyan-500 bg-cyan-50 ring-2 ring-cyan-300' : 'hover:bg-accent/20'}`}
                data-testid="dispose-return">
                <Truck className="h-5 w-5 text-cyan-600" />
                <span className="font-semibold text-cyan-700">Return to Supplier</span>
                <span className="text-[9px] text-muted-foreground text-center">Send back for credit</span>
              </button>
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-semibold">Reason / Notes</Label>
              <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} className="text-xs"
                placeholder={disposition === 'release' ? 'QA approved, batch is safe to use...' : disposition === 'write_off' ? 'Expired beyond recovery, failed inspection...' : 'Supplier credit agreement ref...'}
                data-testid="dispose-reason" />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={submit} disabled={submitting || !disposition}
              className={disposition === 'release' ? 'bg-emerald-600 hover:bg-emerald-700' : disposition === 'write_off' ? 'bg-red-600 hover:bg-red-700' : disposition === 'return_to_supplier' ? 'bg-cyan-600 hover:bg-cyan-700' : ''}
              data-testid="dispose-confirm">
              {submitting ? 'Processing...' : disposition === 'release' ? 'Release to Stock' : disposition === 'write_off' ? 'Submit for Approval' : disposition === 'return_to_supplier' ? 'Return to Supplier' : 'Select Action'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ── Edit Batch Dialog (with serial hold/scrap actions) ──

function EditBatchDialog({ batch, onClose, onSaved }) {
  const [form, setForm] = useState({
    status: batch.status || 'ACTIVE',
    quantity_remaining: String(batch.quantity_remaining ?? ''),
    expiry_date: batch.expiry_date?.slice(0, 10) || '',
    manufactured_date: batch.manufactured_date?.slice(0, 10) || '',
    manufacturer: batch.manufacturer || '',
    lot_number: batch.lot_number || '',
    notes: batch.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [serials, setSerials] = useState(null);
  const [serialsLoading, setSerialsLoading] = useState(false);
  const [actioningSerial, setActioningSerial] = useState(null);

  const loadSerials = useCallback(() => {
    setSerialsLoading(true);
    api.get(`/api/inventory/batches/${batch.batch_number}/serials?item_code=${batch.item_code}&location_code=${batch.location_code}`)
      .then(r => setSerials(r.data))
      .catch(() => setSerials({ summary: {}, data: [] }))
      .finally(() => setSerialsLoading(false));
  }, [batch.batch_number, batch.item_code, batch.location_code]);

  useEffect(() => { loadSerials(); }, [loadSerials]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        quantity_remaining: parseFloat(form.quantity_remaining) || 0,
        item_code: batch.item_code,
        location_code: batch.location_code,
      };
      const r = await api.put(`/api/inventory/batches/${batch.batch_number}/update`, payload);
      toast.success(r.data.message);
      onSaved();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to update')); }
    setSaving(false);
  };

  const serialAction = async (serialNumber, action) => {
    setActioningSerial(serialNumber);
    try {
      const endpoint = `/api/inventory/serials/${serialNumber}/${action}`;
      const r = await api.post(endpoint, { reason: `Action from batch ${batch.batch_number} management` });
      toast.success(r.data.message);
      loadSerials();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
    setActioningSerial(null);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="text-sm">Batch Details — {batch.batch_number}</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2 max-h-[65vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div><span className="text-muted-foreground">Item:</span> <strong>{batch.item_code}</strong></div>
            <div><span className="text-muted-foreground">Location:</span> <strong>{batch.location_code}</strong></div>
            <div><span className="text-muted-foreground">Status:</span> <Badge className={`text-[9px] ${STATUS_STYLES[batch.status]}`}>{batch.status?.replace(/_/g, ' ')}</Badge></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Qty Remaining" type="number" value={form.quantity_remaining} onChange={v => set('quantity_remaining', v)} />
            <Field label="Lot Number" value={form.lot_number} onChange={v => set('lot_number', v)} />
            <Field label="Expiry Date" type="date" value={form.expiry_date} onChange={v => set('expiry_date', v)} />
            <Field label="Manufactured" type="date" value={form.manufactured_date} onChange={v => set('manufactured_date', v)} />
          </div>
          <Field label="Manufacturer" value={form.manufacturer} onChange={v => set('manufacturer', v)} />
          <div>
            <Label className="text-xs font-semibold">Notes</Label>
            <Textarea value={form.notes} onChange={e => set('notes', e.target.value)}
              className="mt-1 text-sm" rows={2} />
          </div>

          {/* Serial numbers section with actions */}
          {serialsLoading ? (
            <div className="text-xs text-muted-foreground text-center py-2">Loading serials...</div>
          ) : serials && serials.data && serials.data.length > 0 ? (
            <div className="border rounded-lg p-2 bg-blue-50/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-blue-700">Serial Numbers ({serials.summary?.total || 0})</span>
                <div className="flex gap-1 flex-wrap">
                  {serials.summary?.in_stock > 0 && <Badge className="bg-emerald-100 text-emerald-700 text-[8px]">{serials.summary.in_stock} In Stock</Badge>}
                  {serials.summary?.on_hold > 0 && <Badge className="bg-orange-100 text-orange-700 text-[8px]">{serials.summary.on_hold} On Hold</Badge>}
                  {serials.summary?.issued > 0 && <Badge className="bg-blue-100 text-blue-700 text-[8px]">{serials.summary.issued} Issued</Badge>}
                  {serials.summary?.scrapped > 0 && <Badge className="bg-red-100 text-red-600 text-[8px]">{serials.summary.scrapped} Scrapped</Badge>}
                  {serials.summary?.written_off > 0 && <Badge className="bg-slate-100 text-slate-600 text-[8px]">{serials.summary.written_off} Written Off</Badge>}
                </div>
              </div>
              <div className="max-h-44 overflow-y-auto">
                <table className="w-full text-[10px]">
                  <thead><tr className="border-b">
                    <th className="text-left px-1.5 py-1 font-semibold">Serial #</th>
                    <th className="text-left px-1.5 py-1 font-semibold">Status</th>
                    <th className="text-left px-1.5 py-1 font-semibold">Issued To</th>
                    <th className="text-right px-1.5 py-1 font-semibold">Actions</th>
                  </tr></thead>
                  <tbody>
                    {serials.data.map((s, i) => {
                      const canHold = s.status === 'IN_STOCK' || s.status === 'RETURNED';
                      const canRelease = s.status === 'ON_HOLD';
                      const canScrap = s.status !== 'SCRAPPED' && s.status !== 'RETURNED_TO_SUPPLIER';
                      const isActioning = actioningSerial === s.serial_number;

                      return (
                        <tr key={i} className="border-b last:border-0 hover:bg-blue-50" data-testid={`serial-row-${s.serial_number}`}>
                          <td className="px-1.5 py-1 font-mono font-semibold">{s.serial_number}</td>
                          <td className="px-1.5 py-1">
                            <Badge className={`text-[8px] ${SERIAL_STATUS_STYLES[s.status] || 'bg-slate-100'}`}>{s.status?.replace(/_/g, ' ')}</Badge>
                          </td>
                          <td className="px-1.5 py-1 text-muted-foreground">{s.issued_to || s.hold_reason || '—'}</td>
                          <td className="px-1.5 py-1 text-right">
                            <div className="flex gap-0.5 justify-end">
                              {canHold && (
                                <Button size="sm" variant="ghost" disabled={isActioning}
                                  className="h-4 text-[8px] px-1 text-orange-600 hover:text-orange-800 hover:bg-orange-50"
                                  onClick={() => serialAction(s.serial_number, 'hold')}
                                  data-testid={`hold-serial-${s.serial_number}`}>
                                  Hold
                                </Button>
                              )}
                              {canRelease && (
                                <Button size="sm" variant="ghost" disabled={isActioning}
                                  className="h-4 text-[8px] px-1 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"
                                  onClick={() => serialAction(s.serial_number, 'release-hold')}
                                  data-testid={`release-serial-${s.serial_number}`}>
                                  Release
                                </Button>
                              )}
                              {canScrap && (
                                <Button size="sm" variant="ghost" disabled={isActioning}
                                  className="h-4 text-[8px] px-1 text-red-600 hover:text-red-800 hover:bg-red-50"
                                  onClick={() => serialAction(s.serial_number, 'scrap')}
                                  data-testid={`scrap-serial-${s.serial_number}`}>
                                  Scrap
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={saving} data-testid="batch-save-btn">
            <Save className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ── Helpers ──

function fmtDate(d, tz = 'America/Los_Angeles') {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz });
  } catch { return d; }
}

function SummaryCard({ label, value, accent = 'slate' }) {
  const colors = { slate: 'text-slate-700', blue: 'text-blue-600', emerald: 'text-emerald-600', red: 'text-red-600', amber: 'text-amber-600', orange: 'text-orange-600' };
  return (
    <div className="border rounded-lg p-2 bg-background">
      <p className={`text-lg font-bold ${colors[accent] || colors.slate}`}>{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}

function CreateBatchDialog({ open, onClose, itemCode, onCreated }) {
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({
    batch_number: '', location_code: '', quantity: '', expiry_date: '',
    manufactured_date: '', manufacturer: '', lot_number: '', notes: '',
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) {
      api.get('/api/inventory/locations').then(r => setLocations(r.data.data || [])).catch(() => {});
      setForm({ batch_number: '', location_code: '', quantity: '', expiry_date: '',
        manufactured_date: '', manufacturer: '', lot_number: '', notes: '' });
    }
  }, [open]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.batch_number.trim()) { toast.error('Batch number is required'); return; }
    if (!form.location_code) { toast.error('Select a location'); return; }
    if (!form.quantity || parseFloat(form.quantity) <= 0) { toast.error('Quantity must be > 0'); return; }
    setCreating(true);
    try {
      const r = await api.post(`/api/inventory/items/${itemCode}/batches`, {
        ...form, quantity: parseFloat(form.quantity),
      });
      toast.success(r.data.message);
      onCreated();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to create batch')); }
    setCreating(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Receive New Batch — {itemCode}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 pt-2">
          <Field label="Batch Number *" value={form.batch_number} onChange={v => set('batch_number', v)} testId="batch-number-input" />
          <div>
            <Label className="text-xs font-semibold">Location *</Label>
            <select value={form.location_code} onChange={e => set('location_code', e.target.value)}
              className="w-full mt-1 h-9 px-3 border rounded-md text-sm bg-background"
              data-testid="batch-location-select">
              <option value="">Select...</option>
              {locations.filter(l => l.active !== false).map(l => (
                <option key={l.location_code} value={l.location_code}>{l.location_code}</option>
              ))}
            </select>
          </div>
          <Field label="Quantity Received *" type="number" value={form.quantity} onChange={v => set('quantity', v)} testId="batch-qty-input" />
          <Field label="Lot Number" value={form.lot_number} onChange={v => set('lot_number', v)} />
          <Field label="Expiry Date" type="date" value={form.expiry_date} onChange={v => set('expiry_date', v)} testId="batch-expiry-input" />
          <Field label="Manufactured Date" type="date" value={form.manufactured_date} onChange={v => set('manufactured_date', v)} />
          <Field label="Manufacturer" value={form.manufacturer} onChange={v => set('manufacturer', v)} />
          <div className="col-span-2">
            <Label className="text-xs font-semibold">Notes</Label>
            <Textarea value={form.notes} onChange={e => set('notes', e.target.value)}
              placeholder="Optional notes..." className="mt-1 text-sm" rows={2} />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={creating} data-testid="batch-create-submit">
            {creating ? 'Creating...' : 'Receive Batch'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = 'text', testId }) {
  return (
    <div>
      <Label className="text-xs font-semibold">{label}</Label>
      <Input type={type} value={value} onChange={e => onChange(e.target.value)}
        className="mt-1 h-8 text-sm" data-testid={testId} />
    </div>
  );
}
